import { useState } from 'react'
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Radio,
  Sparkles,
  TrendingDown,
  TrendingUp,
  Wifi,
  Zap,
} from 'lucide-react'
import { Button, Select } from '../ui'
import { HEATMAP_LAYERS } from '../../lib/floorPlan/materials'
import { recommendationFixLabel, hasAutoFix } from '../../lib/floorPlan/rfRecommendationFixes'
import type { FloorPlanMetrics, FloorPlanOptimizationResult } from '../../lib/floorPlan/rfOptimizer'
import type { RequirementPassSummary } from '../../lib/floorPlan/rfFloorMetrics'
import type { InterferencePlanStatus } from '../../lib/floorPlan/rfInterferencePolicy'
import type { FloorGeometry, FloorRecommendation } from '../../lib/floorPlan/types'

type Props = {
  placedApCount: number
  bandGHz: number
  geometry: FloorGeometry
  metrics: FloorPlanMetrics
  requirementPass?: RequirementPassSummary | null
  interferencePlan?: InterferencePlanStatus | null
  optimizationResult: FloorPlanOptimizationResult | null
  recommendations: FloorRecommendation[]
  optimizing: boolean
  optimizationOverlayOpen: boolean
  applyingRecommendationId: string | null
  floorId: string
  onGeometryPatch: (patch: Partial<FloorGeometry>) => void
  onOptimize: () => void
  onApplyRecommendation: (rec: FloorRecommendation) => void
  onDismissOptimization?: () => void
}

function DeltaBadge({ before, after, invert }: { before: number; after: number; invert?: boolean }) {
  const delta = Math.round(after - before)
  if (delta === 0) return null
  const improved = invert ? delta < 0 : delta > 0
  const Icon = improved ? TrendingUp : TrendingDown
  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-semibold ${
        improved ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'
      }`}
    >
      <Icon size={10} />
      {delta > 0 ? '+' : ''}
      {delta}%
    </span>
  )
}

function categoryIcon(category?: FloorRecommendation['category']) {
  switch (category) {
    case 'coverage':
      return Wifi
    case 'interference':
      return Zap
    case 'modeling':
      return Radio
    default:
      return AlertTriangle
  }
}

function priorityStyles(priority: string) {
  if (priority === 'HIGH') return 'border-red-200 bg-red-50/80 dark:border-red-900 dark:bg-red-950/30'
  if (priority === 'MEDIUM') return 'border-amber-200 bg-amber-50/80 dark:border-amber-900 dark:bg-amber-950/30'
  return 'border-slate-200 bg-slate-50/80 dark:border-slate-700 dark:bg-slate-900/40'
}

export default function FloorPlanRfPanel({
  placedApCount,
  bandGHz,
  geometry,
  metrics,
  requirementPass,
  interferencePlan,
  optimizationResult,
  recommendations,
  optimizing,
  optimizationOverlayOpen,
  applyingRecommendationId,
  floorId,
  onGeometryPatch,
  onOptimize,
  onApplyRecommendation,
  onDismissOptimization,
}: Props) {
  const after = optimizationResult?.metricsAfter
  const showResult = optimizationResult && !optimizationOverlayOpen
  const highCount = recommendations.filter(r => r.priority === 'HIGH').length
  const [recsOpen, setRecsOpen] = useState(highCount > 0)
  const [optResultOpen, setOptResultOpen] = useState(true)

  return (
    <div className="space-y-2">
      <div className="rounded-lg border border-mg-border bg-mg-page p-2.5">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-mg-muted">RF display</p>
        <div className="mt-1.5 grid gap-1.5 sm:grid-cols-2">
          <div>
            <label className="text-xs text-mg-muted">Heatmap layer</label>
            <Select
              value={geometry.heatmapLayer}
              className="mt-1 w-full"
              onChange={e =>
                onGeometryPatch({
                  heatmapLayer: e.target.value as FloorGeometry['heatmapLayer'],
                  layers: { ...geometry.layers, heatmap: true },
                })
              }
            >
              {HEATMAP_LAYERS.map(h => (
                <option key={h.id} value={h.id}>
                  {h.label}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="text-xs text-mg-muted">Band</label>
            <Select
              value={String(geometry.bandGHz)}
              className="mt-1 w-full"
              onChange={e => onGeometryPatch({ bandGHz: Number(e.target.value) })}
            >
              <option value="2.4">2.4 GHz</option>
              <option value="5">5 GHz</option>
              <option value="6">6 GHz</option>
            </Select>
          </div>
        </div>
        <label className="mt-2 flex items-center gap-2 text-[11px] text-mg-muted">
          <input
            type="checkbox"
            checked={geometry.showSuggestedPlacements !== false}
            onChange={e => onGeometryPatch({ showSuggestedPlacements: e.target.checked })}
          />
          Show suggested AP placements on map
        </label>
      </div>

      {interferencePlan?.tuned && (
        <div className="rounded-lg border border-emerald-200/80 bg-emerald-50/60 p-2.5 dark:border-emerald-900 dark:bg-emerald-950/30">
          <p className="text-xs font-semibold text-emerald-900 dark:text-emerald-100">RF plan tuned</p>
          <p className="mt-1 text-[11px] leading-snug text-emerald-800 dark:text-emerald-200">
            Channels assigned · avg TX {Math.round(interferencePlan.avgTxDbm)} dBm. Transition zones up to ~
            {interferencePlan.expectedHighInterfPct}% are normal for this AP count — not a failed optimization.
          </p>
        </div>
      )}

      <div className="grid grid-cols-3 gap-1.5 rounded-lg border border-mg-border bg-mg-surface p-2">
        <div className="text-center">
          <p className="text-[10px] text-mg-muted">Good</p>
          <p className="text-sm font-bold tabular-nums text-emerald-600">{Math.round(metrics.goodPct)}%</p>
        </div>
        <div className="text-center">
          <p className="text-[10px] text-mg-muted">Weak</p>
          <p className="text-sm font-bold tabular-nums text-red-600">{Math.round(metrics.weakPct)}%</p>
        </div>
        <div className="text-center">
          <p className="text-[10px] text-mg-muted">Interf.</p>
          <p className="text-sm font-bold tabular-nums text-amber-600">{Math.round(metrics.highInterfPct)}%</p>
        </div>
      </div>

      {requirementPass && requirementPass.zones.length > 0 && (
        <div className="rounded-lg border border-emerald-200/80 bg-emerald-50/50 p-2.5 dark:border-emerald-900 dark:bg-emerald-950/20">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-800 dark:text-emerald-200">
            Requirement zones
          </p>
          <p className="mt-1 text-sm font-bold tabular-nums text-emerald-700 dark:text-emerald-300">
            {Math.round(requirementPass.overallPassPct)}% pass overall
          </p>
          <ul className="mt-2 max-h-28 space-y-1 overflow-y-auto text-[11px] text-emerald-900 dark:text-emerald-100">
            {requirementPass.zones.map(z => (
              <li key={z.roomId} className="flex justify-between gap-2">
                <span className="truncate">{z.name}</span>
                <span className="shrink-0 tabular-nums font-semibold">
                  {z.passPct == null
                    ? 'No samples'
                    : `${Math.round(z.passPct)}% ≥ ${z.minRssiDbm} dBm`}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {optimizationOverlayOpen ? (
        <p className="rounded-md border border-emerald-200/80 bg-emerald-50/50 px-2 py-1.5 text-center text-[10px] text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/20 dark:text-emerald-100">
          {optimizing ? 'Progress on map…' : 'Review summary on map, then OK.'}
        </p>
      ) : (
        <>
          <Button
            variant="primary"
            size="sm"
            type="button"
            className="h-8 w-full text-xs"
            disabled={!floorId || placedApCount === 0 || !!applyingRecommendationId}
            onClick={onOptimize}
          >
            <Sparkles size={12} />
            Optimize floor plan
          </Button>
          <p className="text-[10px] leading-snug text-mg-muted">
            Optimize adjusts power, channels, and placement on existing APs. To add an AP, use{' '}
            <strong className="font-semibold text-mg-text">Apply fix</strong> on a coverage recommendation — you
            confirm before anything is placed.
          </p>
        </>
      )}

      {showResult && (
          <div className="mt-2 overflow-hidden rounded-lg border border-emerald-200/80 bg-white/70 dark:border-emerald-800 dark:bg-black/20">
            <button
              type="button"
              className="flex w-full items-center gap-2 px-2.5 py-2 text-left hover:bg-emerald-50/50 dark:hover:bg-emerald-950/30"
              aria-expanded={optResultOpen}
              onClick={() => setOptResultOpen(o => !o)}
            >
              <ChevronDown
                size={14}
                className={`shrink-0 text-emerald-700 transition-transform dark:text-emerald-300 ${
                  optResultOpen ? 'rotate-0' : '-rotate-90'
                }`}
              />
              <CheckCircle2 size={14} className="shrink-0 text-emerald-600" />
              <span className="min-w-0 flex-1 text-xs font-semibold text-emerald-800 dark:text-emerald-200">
                Last optimization
              </span>
              {after && !optResultOpen && (
                <span className="shrink-0 text-[10px] tabular-nums text-emerald-800 dark:text-emerald-200">
                  weak {Math.round(optimizationResult.metricsBefore.weakPct)}% → {Math.round(after.weakPct)}%
                </span>
              )}
              {onDismissOptimization && (
                <span
                  role="button"
                  tabIndex={0}
                  className="shrink-0 text-[10px] text-mg-muted hover:text-mg-text"
                  onClick={e => {
                    e.stopPropagation()
                    onDismissOptimization()
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.stopPropagation()
                      onDismissOptimization?.()
                    }
                  }}
                >
                  Dismiss
                </span>
              )}
            </button>

            {optResultOpen && after && (
              <div className="space-y-2 border-t border-emerald-200/60 px-2.5 pb-2.5 pt-2 dark:border-emerald-800/60">
                <div className="grid grid-cols-2 gap-2 text-[11px]">
                  <div className="rounded-md bg-emerald-50/80 px-2 py-1.5 dark:bg-emerald-950/40">
                    <span className="text-mg-muted">Good</span>
                    <div className="mt-0.5 flex flex-wrap items-center gap-1 font-semibold text-mg-text">
                      {Math.round(optimizationResult.metricsBefore.goodPct)}% → {Math.round(after.goodPct)}%
                      <DeltaBadge before={optimizationResult.metricsBefore.goodPct} after={after.goodPct} />
                    </div>
                  </div>
                  <div className="rounded-md bg-emerald-50/80 px-2 py-1.5 dark:bg-emerald-950/40">
                    <span className="text-mg-muted">Weak</span>
                    <div className="mt-0.5 flex flex-wrap items-center gap-1 font-semibold text-mg-text">
                      {Math.round(optimizationResult.metricsBefore.weakPct)}% → {Math.round(after.weakPct)}%
                      <DeltaBadge before={optimizationResult.metricsBefore.weakPct} after={after.weakPct} invert />
                    </div>
                  </div>
                </div>
                <ul className="max-h-32 space-y-1 overflow-y-auto text-[11px] leading-snug text-emerald-900 dark:text-emerald-100">
                  {optimizationResult.summary.map(line => (
                    <li key={line} className="flex gap-1.5">
                      <span className="text-emerald-600">•</span>
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

      <div className="overflow-hidden rounded-lg border border-mg-border bg-mg-page">
        <button
          type="button"
          className="flex w-full items-center gap-2 px-3 py-2.5 text-left transition-colors hover:bg-mg-border/20"
          aria-expanded={recsOpen}
          onClick={() => setRecsOpen(open => !open)}
        >
          <ChevronDown
            size={16}
            className={`shrink-0 text-mg-muted transition-transform ${recsOpen ? 'rotate-0' : '-rotate-90'}`}
          />
          <AlertTriangle size={14} className="shrink-0 text-amber-500" />
          <span className="flex-1 text-xs font-semibold text-mg-text">Recommendations</span>
          {recommendations.length > 0 && (
            <span className="shrink-0 rounded-full bg-mg-border/40 px-2 py-0.5 text-[10px] font-semibold tabular-nums">
              {recommendations.length}
            </span>
          )}
        </button>

        {recsOpen && (
          <div className="max-h-64 space-y-2 overflow-y-auto border-t border-mg-border px-3 pb-3 pt-2">
            {placedApCount === 0 && (
              <p className="rounded-lg border border-dashed border-mg-border px-3 py-4 text-center text-[11px] text-mg-muted">
                Place at least one AP to generate recommendations.
              </p>
            )}
            {placedApCount > 0 && recommendations.length === 0 && (
              <p className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-3 text-[11px] text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-100">
                <CheckCircle2 size={14} />
                No issues detected.
              </p>
            )}
            {recommendations.map(r => {
              const Icon = categoryIcon(r.category)
              return (
                <div key={r.id} className={`rounded-lg border px-2.5 py-2 ${priorityStyles(r.priority)}`}>
                  <div className="flex items-start gap-2">
                    <Icon size={14} className="mt-0.5 shrink-0 text-mg-muted" />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="rounded bg-black/5 px-1.5 py-0.5 text-[9px] font-bold uppercase dark:bg-white/5">
                          {r.priority}
                        </span>
                      </div>
                      <p className="mt-1 text-xs font-semibold text-mg-text">{r.title}</p>
                      <p className="mt-0.5 text-[11px] leading-relaxed text-mg-muted">{r.detail}</p>
                      {hasAutoFix(r) ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          type="button"
                          className="mt-2 w-full"
                          disabled={!!applyingRecommendationId || optimizing}
                          onClick={() => onApplyRecommendation(r)}
                        >
                          {applyingRecommendationId === r.id ? 'Applying…' : recommendationFixLabel(r)}
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
